/**
 * WorkingCell — "the cell at work", row G of `thinking/design/toile_wall/WorkingStates.dc.html`
 * ("three signs, one motion"), extracted so the wall tile and the armed view (`Column.tsx` desktop,
 * `Sheet.tsx` phone) paint the SAME row the same way instead of each carrying its own idea of
 * "working" (defect: the armed result slot still showed an older shimmer with no place and no
 * frames while `Tile.tsx` had already moved to row G — operator on toile-dev, 2026-09-12).
 *
 * Two questions, in order — see `Tile.tsx`'s own header doc for the full reasoning, unchanged by
 * the extraction:
 * - **Is it alive?** The breath (`.breath`), shared by every unfinished row, in phase. Queued and
 *   claimed/rendering read identically — the breath alone never distinguishes them.
 * - **Is mine coming?** The frames (`previewFrame`): once a frame exists it replaces the breath
 *   outright, as the service made it (no filter, no ramp).
 * Between the two sits the place (`.numeral`), the row's only text: dim, `--tl-text` at 1 (the row
 * about to be claimed or already on the machine — the caller hands a `rendering` row place 1, same
 * contract everywhere this renders), and it holds through the claimed gap until a frame replaces
 * it.
 *
 * The done-transition hairline stays `Tile.tsx`'s own concern — it fires on a `working` -> `done`
 * transition the armed view does not need to paint the same way, and is not part of "the cell at
 * work" itself (row G names the hairline separately, "once more, after the picture").
 */
import styles from './WorkingCell.module.scss'

/** One loaded preview frame — `usePreviewFrames.ts` hands these up, ordinal `n` as the generation
 *  service emits them (never the diffusion step). */
export type PreviewFrame = {url: string; n: 1 | 2 | 3}

export type WorkingCellProps = {
    /** 1-based place in the user's own queued order, or 1 for a row already claimed/rendering
     *  (callers derive this the same way `Wall.tsx`'s tile render site does — off `queuedOrder`, or
     *  1 for a `rendering` row). `null`/absent once a frame exists or there is no place to show. */
    queuePlace?: number | null
    /** The row's newest loaded frame. `null`/absent before frame 1 has loaded. Mutually exclusive
     *  with the place: once a frame exists it replaces the numeral outright. */
    previewFrame?: PreviewFrame | null
}

export function WorkingCell({queuePlace, previewFrame}: WorkingCellProps) {
    const displayPlace = previewFrame ? null : (queuePlace ?? null)
    return (
        <>
            {previewFrame ? (
                <img src={previewFrame.url} alt="" aria-hidden="true" className={styles.frame} />
            ) : (
                <span className={styles.breath} aria-hidden="true" />
            )}
            {displayPlace != null && (
                <span
                    className={displayPlace === 1 ? `${styles.numeral} ${styles.numeralFirst}` : styles.numeral}
                    aria-hidden="true"
                >
                    {displayPlace}
                </span>
            )}
        </>
    )
}
